import React, { useState, useEffect, useRef } from 'react';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { Upload, Trash2, Save, Image, Settings } from 'lucide-react';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

export default function ConfiguracionPage() {
  const [config, setConfig] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [textos, setTextos] = useState({ nombre_empresa_transporte:'', nombre_proveedor_gps:'', zona_horaria:'America/Mexico_City', tiempo_alerta_sin_atencion:'30' });
  const logoTransRef = useRef();
  const logoProvRef = useRef();

  const fetchConfig = async () => {
    setLoading(true);
    try {
      const res = await api.get('/configuracion');
      setConfig(res.data.data);
      setTextos({
        nombre_empresa_transporte: res.data.data.nombre_empresa_transporte?.valor || '',
        nombre_proveedor_gps: res.data.data.nombre_proveedor_gps?.valor || '',
        zona_horaria: res.data.data.zona_horaria?.valor || 'America/Mexico_City',
        tiempo_alerta_sin_atencion: res.data.data.tiempo_alerta_sin_atencion?.valor || '30'
      });
    } catch(e) { toast.error('Error al cargar configuración'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchConfig(); }, []);

  const handleSaveTextos = async () => {
    setSaving(true);
    try {
      await Promise.all(Object.entries(textos).map(([clave, valor]) =>
        api.put(`/configuracion/${clave}`, { valor })
      ));
      toast.success('Configuración guardada');
      fetchConfig();
    } catch(e) { toast.error('Error al guardar'); }
    finally { setSaving(false); }
  };

  const handleUploadLogo = async (clave, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('logo', file);
    try {
      await api.post(`/configuracion/logo/${clave}`, fd, { headers:{'Content-Type':'multipart/form-data'} });
      toast.success('Logo actualizado');
      fetchConfig();
    } catch(e) { toast.error(e.response?.data?.message || 'Error al subir logo'); }
  };

  const handleDeleteLogo = async (clave) => {
    if (!window.confirm('¿Eliminar el logo?')) return;
    try {
      await api.delete(`/configuracion/logo/${clave}`);
      toast.success('Logo eliminado');
      fetchConfig();
    } catch(e) { toast.error('Error'); }
  };

  const logoUrl = (clave) => {
    const val = config[clave]?.valor;
    if (!val) return null;
    return `${API_BASE.replace('/api','')}${API_BASE}/uploads/${val}`.replace('/api/api','/api');
  };

  if (loading) return <div className="loading-screen"><div className="spinner"/></div>;

  return (
    <div>
      <div className="page-header">
        <div>
          <div className="page-title">Configuración</div>
          <div className="page-subtitle">Ajustes del sistema y branding</div>
        </div>
      </div>

      <div style={{ display:'flex', flexDirection:'column', gap:20, maxWidth:800 }}>
        {/* Datos generales */}
        <div className="card">
          <div className="card-header"><span className="card-title"><Settings size={14} style={{marginRight:6}}/>Datos Generales</span></div>
          <div className="card-body">
            <div className="grid-2">
              <div className="form-group">
                <label className="form-label">Nombre de la Empresa Transportista</label>
                <input className="form-control" value={textos.nombre_empresa_transporte} onChange={e => setTextos(t=>({...t,nombre_empresa_transporte:e.target.value}))} placeholder="Transportes S.A. de C.V."/>
              </div>
              <div className="form-group">
                <label className="form-label">Nombre del Proveedor GPS</label>
                <input className="form-control" value={textos.nombre_proveedor_gps} onChange={e => setTextos(t=>({...t,nombre_proveedor_gps:e.target.value}))} placeholder="GPS Monitor Pro"/>
              </div>
              <div className="form-group">
                <label className="form-label">Zona Horaria</label>
                <select className="form-control" value={textos.zona_horaria} onChange={e => setTextos(t=>({...t,zona_horaria:e.target.value}))}>
                  <option value="America/Mexico_City">Ciudad de México (CST/CDT)</option>
                  <option value="America/Monterrey">Monterrey (CST/CDT)</option>
                  <option value="America/Tijuana">Tijuana (PST/PDT)</option>
                  <option value="America/Hermosillo">Hermosillo (MST)</option>
                  <option value="America/Cancun">Cancún (EST)</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Minutos sin atención antes de escalar</label>
                <input className="form-control" type="number" value={textos.tiempo_alerta_sin_atencion} onChange={e => setTextos(t=>({...t,tiempo_alerta_sin_atencion:e.target.value}))} min="5" max="120"/>
              </div>
            </div>
            <button className="btn btn-primary" onClick={handleSaveTextos} disabled={saving}>
              {saving ? <span className="spinner" style={{width:14,height:14}}/> : <Save size={14}/>} Guardar Cambios
            </button>
          </div>
        </div>

        {/* Logos */}
        <div className="card">
          <div className="card-header"><span className="card-title"><Image size={14} style={{marginRight:6}}/>Logos para Reportes</span></div>
          <div className="card-body">
            <div className="grid-2">
              {/* Logo Transportista */}
              <div>
                <div className="form-label" style={{marginBottom:12}}>Logo Empresa Transportista</div>
                <div style={{ background:'var(--bg-elevated)', border:'1px dashed var(--border-light)', borderRadius:'var(--radius-lg)', padding:20, display:'flex', flexDirection:'column', alignItems:'center', gap:12, minHeight:140 }}>
                  {logoUrl('logo_transportista') ? (
                    <>
                      <img src={logoUrl('logo_transportista')} alt="Logo transportista" style={{ maxHeight:80, maxWidth:'100%', objectFit:'contain' }} onError={e=>e.target.style.display='none'}/>
                      <div style={{display:'flex',gap:8}}>
                        <button className="btn btn-secondary btn-sm" onClick={() => logoTransRef.current.click()}><Upload size={12}/> Cambiar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteLogo('logo_transportista')}><Trash2 size={12}/> Eliminar</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ color:'var(--text-muted)', fontSize:12, textAlign:'center' }}>Sin logo configurado<br/><span style={{fontSize:11}}>JPG, PNG, SVG — Máx. 2MB</span></div>
                      <button className="btn btn-secondary btn-sm" onClick={() => logoTransRef.current.click()}><Upload size={12}/> Subir Logo</button>
                    </>
                  )}
                  <input ref={logoTransRef} type="file" accept="image/*" style={{display:'none'}} onChange={e => handleUploadLogo('logo_transportista', e.target.files[0])}/>
                </div>
              </div>

              {/* Logo Proveedor GPS */}
              <div>
                <div className="form-label" style={{marginBottom:12}}>Logo Proveedor GPS</div>
                <div style={{ background:'var(--bg-elevated)', border:'1px dashed var(--border-light)', borderRadius:'var(--radius-lg)', padding:20, display:'flex', flexDirection:'column', alignItems:'center', gap:12, minHeight:140 }}>
                  {logoUrl('logo_proveedor') ? (
                    <>
                      <img src={logoUrl('logo_proveedor')} alt="Logo proveedor" style={{ maxHeight:80, maxWidth:'100%', objectFit:'contain' }} onError={e=>e.target.style.display='none'}/>
                      <div style={{display:'flex',gap:8}}>
                        <button className="btn btn-secondary btn-sm" onClick={() => logoProvRef.current.click()}><Upload size={12}/> Cambiar</button>
                        <button className="btn btn-danger btn-sm" onClick={() => handleDeleteLogo('logo_proveedor')}><Trash2 size={12}/> Eliminar</button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div style={{ color:'var(--text-muted)', fontSize:12, textAlign:'center' }}>Sin logo configurado<br/><span style={{fontSize:11}}>JPG, PNG, SVG — Máx. 2MB</span></div>
                      <button className="btn btn-secondary btn-sm" onClick={() => logoProvRef.current.click()}><Upload size={12}/> Subir Logo</button>
                    </>
                  )}
                  <input ref={logoProvRef} type="file" accept="image/*" style={{display:'none'}} onChange={e => handleUploadLogo('logo_proveedor', e.target.files[0])}/>
                </div>
              </div>
            </div>
            <div style={{ marginTop:16, padding:12, background:'var(--bg-elevated)', borderRadius:'var(--radius)', fontSize:12, color:'var(--text-muted)' }}>
              <strong style={{color:'var(--text-secondary)'}}>ℹ️ Información:</strong> Los logos se incluirán automáticamente en los reportes PDF y fichas de evento. Se recomienda usar imágenes PNG con fondo transparente para mejores resultados.
            </div>
          </div>
        </div>

        {/* Info Webhook */}
        <div className="card">
          <div className="card-header"><span className="card-title">Información del Webhook GPS</span></div>
          <div className="card-body">
            <p style={{ fontSize:13, color:'var(--text-secondary)', marginBottom:16 }}>Proporciona esta información al proveedor del sistema GPS para activar el recibo automático de alertas:</p>
            <div className="form-group">
              <label className="form-label">URL del Webhook</label>
              <div style={{ display:'flex', gap:8 }}>
                <input className="form-control font-mono" readOnly value={`${window.location.protocol}//${window.location.hostname}:3001/api/webhook/alerta`} style={{fontSize:12}}/>
                <button className="btn btn-secondary btn-sm" onClick={() => { navigator.clipboard.writeText(`${window.location.protocol}//${window.location.hostname}:3001/api/webhook/alerta`); toast.success('Copiado'); }}>Copiar</button>
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Header de Autenticación</label>
              <input className="form-control font-mono" readOnly value="X-Webhook-Token: [WEBHOOK_SECRET del archivo .env]" style={{fontSize:12}}/>
            </div>
            <div style={{ padding:12, background:'var(--accent-glow)', border:'1px solid var(--border-light)', borderRadius:'var(--radius)', fontSize:12, color:'var(--text-secondary)' }}>
              <strong style={{color:'var(--accent)'}}>Método:</strong> POST &nbsp;|&nbsp; <strong style={{color:'var(--accent)'}}>Content-Type:</strong> application/json<br/>
              El sistema acepta payloads flexibles del proveedor GPS. Consulta el README del backend para la estructura completa.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
